import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatTL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilePdf, FileXls, CashRegister, Sparkle, Coins, Warning, Money, CreditCard, Bank, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Kasa() {
  const { user } = useAuth();
  const isFullAdmin = user?.role === "admin" || user?.role === "super_admin";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [pending, setPending] = useState(null);
  const [balance, setBalance] = useState(0);
  const [balanceDetail, setBalanceDetail] = useState(null);
  const [lot, setLot] = useState("all");
  const [period, setPeriod] = useState("daily");

  const load = async () => {
    try {
      const q = lot === "all" ? "" : `?lot=${lot}`;
      const [{ data: kasa }, pRes, bRes] = await Promise.all([
        api.get(`/kasa/today${q}`),
        isFullAdmin ? api.get("/bank-transfers/pending") : Promise.resolve({ data: null }),
        isFullAdmin ? api.get("/bank-transfers/balance") : Promise.resolve({ data: { balance: 0 } }),
      ]);
      setData(kasa);
      setPending(pRes.data);
      setBalance(bRes.data?.balance || 0);
      setBalanceDetail(bRes.data || null);
    } catch { toast.error("Kasa yüklenemedi"); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lot]);

  const downloadFile = async (fmt) => {
    try {
      const token = localStorage.getItem("otopark_token");
      const res = await fetch(
        `${BACKEND_URL}/api/reports/export/${fmt}?period=${period}${lot === "all" ? "" : `&lot=${lot}`}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `otopark_${period}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("İndirilemedi"); }
  };

  if (!data) return <div className="p-8"><div className="pulse-dot" /></div>;

  const methods = data.by_method || {};
  const grand = data.grand_total_collected || 0;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Kasa</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Kasa & Gelir</h1>
          <div className="text-sm text-muted-foreground mt-1">Bugün: {data.date}</div>
        </div>
        <Select value={lot} onValueChange={setLot}>
          <SelectTrigger className="w-40 rounded-sm" data-testid="kasa-lot-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Otoparklar</SelectItem>
            <SelectItem value="ust_kat">Üst Kat</SelectItem>
            <SelectItem value="alt_kat">Alt Kat</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Genel Kasa Toplamı — tüm zamanlar net (brüt gelir − toplam giderler) */}
      <Card className="rounded-sm border-primary/40 bg-primary/5">
        <CardContent className="p-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Kasa Toplamı</div>
            <div className="text-5xl font-bold font-mono text-primary mt-2" data-testid="kasa-grand-total">
              {formatTL(data.kasa_net_total)}
            </div>
            <div className="text-sm text-muted-foreground mt-2">
              Bugün: {data.exits_today} çıkış · {data.entries_today} giriş
            </div>
          </div>
          <div className="text-right space-y-1">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Otopark Ücretleri (Bugün)</div>
              <div className="text-lg font-mono font-bold" data-testid="kasa-parking-total">
                {formatTL(data.parking_total || 0)} <span className="text-xs text-muted-foreground">({data.parking_count || 0})</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-primary">Abonelik Tahsilatları (Bugün)</div>
              <div className="text-lg font-mono font-bold text-primary" data-testid="kasa-subscription-total">
                {formatTL(data.subscription_total || 0)} <span className="text-xs text-muted-foreground">({data.subscription_count || 0})</span>
              </div>
            </div>
          </div>
          <CashRegister size={64} weight="fill" className="text-primary hidden md:block" />
        </CardContent>
      </Card>

      {/* BANKA AKTARIM BUTONU - sadece admin/super_admin */}
      {isFullAdmin && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/bank-transfers")}
            data-testid="kasa-goto-bank-transfers"
            className="rounded-sm uppercase tracking-widest font-semibold text-xs">
            <Bank size={14} weight="fill" /> Banka Aktarımları & Giderler <ArrowRight size={14} weight="bold" />
          </Button>
        </div>
      )}

      {/* ÖDEME YÖNTEMİ KIRILIMI - 3 büyük kart. Değerler ELDE BEKLEYEN (henüz
          bankaya aktarılmamış) tutar. Aktarım yapılınca otomatik düşer. */}
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-widest mb-3 text-muted-foreground">
          Elde Bekleyen (Aktarılmamış)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="rounded-sm border-green-400/50 bg-green-50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Money size={26} weight="fill" className="text-green-600" />
                  <div className="text-xs uppercase tracking-widest text-green-800 font-semibold">Kasada Nakit</div>
                </div>
                <div className="text-[10px] text-green-700 uppercase tracking-widest">{(pending?.cash?.count) ?? (methods.cash?.count || 0)} işlem</div>
              </div>
              <div className="text-4xl font-bold font-mono text-green-700" data-testid="kasa-cash-total">
                {formatTL((pending?.cash?.amount) ?? (methods.cash?.amount || 0))}
              </div>
              {pending?.cash && (pending.cash.parking_amount > 0 || pending.cash.subscription_amount > 0) && (
                <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]" data-testid="kasa-cash-breakdown">
                  <div className="bg-white/60 rounded-sm p-2 border border-green-200/60">
                    <div className="uppercase tracking-widest text-green-800/70 text-[9px]">Otopark</div>
                    <div className="font-mono font-bold text-green-800">{formatTL(pending.cash.parking_amount)}</div>
                    <div className="text-[9px] text-green-700/70">{pending.cash.parking_count} tahsilat</div>
                  </div>
                  <div className="bg-white/60 rounded-sm p-2 border border-green-200/60">
                    <div className="uppercase tracking-widest text-green-800/70 text-[9px]">Abonelik</div>
                    <div className="font-mono font-bold text-green-800">{formatTL(pending.cash.subscription_amount)}</div>
                    <div className="text-[9px] text-green-700/70">{pending.cash.subscription_count} tahsilat</div>
                  </div>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-green-200 text-[10px] text-green-800/70">
                Aktarım yapıldığında sıfırlanır · Giderler düşülmüş
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-sm border-blue-400/50 bg-blue-50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CreditCard size={26} weight="fill" className="text-blue-600" />
                  <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">Kredi Kartı</div>
                </div>
                <div className="text-[10px] text-blue-700 uppercase tracking-widest">{(pending?.card?.count) ?? (methods.card?.count || 0)} çekim</div>
              </div>
              <div className="text-4xl font-bold font-mono text-blue-700" data-testid="kasa-card-total">
                {formatTL((pending?.card?.amount) ?? (methods.card?.amount || 0))}
              </div>
              {pending?.card && (pending.card.parking_amount > 0 || pending.card.subscription_amount > 0) && (
                <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]" data-testid="kasa-card-breakdown">
                  <div className="bg-white/60 rounded-sm p-2 border border-blue-200/60">
                    <div className="uppercase tracking-widest text-blue-800/70 text-[9px]">Otopark</div>
                    <div className="font-mono font-bold text-blue-800">{formatTL(pending.card.parking_amount)}</div>
                    <div className="text-[9px] text-blue-700/70">{pending.card.parking_count} tahsilat</div>
                  </div>
                  <div className="bg-white/60 rounded-sm p-2 border border-blue-200/60">
                    <div className="uppercase tracking-widest text-blue-800/70 text-[9px]">Abonelik</div>
                    <div className="font-mono font-bold text-blue-800">{formatTL(pending.card.subscription_amount)}</div>
                    <div className="text-[9px] text-blue-700/70">{pending.card.subscription_count} tahsilat</div>
                  </div>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-blue-200 text-[10px] text-blue-800/70">
                Aktarım yapıldığında sıfırlanır · Giderler düşülmüş
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-sm border-purple-400/50 bg-purple-50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Bank size={26} weight="fill" className="text-purple-600" />
                  <div className="text-xs uppercase tracking-widest text-purple-800 font-semibold">Banka Hesabı</div>
                </div>
                <div className="text-[10px] text-purple-700 uppercase tracking-widest">{isFullAdmin ? "TOPLAM" : `${methods.bank_transfer?.count || 0} transfer`}</div>
              </div>
              <div className="text-4xl font-bold font-mono text-purple-700" data-testid="kasa-bank-total">
                {formatTL(isFullAdmin ? balance : (methods.bank_transfer?.amount || 0))}
              </div>
              {isFullAdmin && balanceDetail && ((balanceDetail.bank_parking || 0) > 0 || (balanceDetail.bank_subscription || 0) > 0) && (
                <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]" data-testid="kasa-bank-breakdown">
                  <div className="bg-white/60 rounded-sm p-2 border border-purple-200/60">
                    <div className="uppercase tracking-widest text-purple-800/70 text-[9px]">Otopark</div>
                    <div className="font-mono font-bold text-purple-800">{formatTL(balanceDetail.bank_parking || 0)}</div>
                  </div>
                  <div className="bg-white/60 rounded-sm p-2 border border-purple-200/60">
                    <div className="uppercase tracking-widest text-purple-800/70 text-[9px]">Abonelik</div>
                    <div className="font-mono font-bold text-purple-800">{formatTL(balanceDetail.bank_subscription || 0)}</div>
                  </div>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-purple-200 text-[10px] text-purple-800/70">
                EFT/Havale + nakit/kart aktarımları − banka giderleri
              </div>
            </CardContent>
          </Card>
        </div>
        {(methods.wallet?.amount > 0 || methods.other?.amount > 0) && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            {methods.wallet?.amount > 0 && (
              <Card className="rounded-sm border-border">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Müşteri Cüzdanı</div>
                    <div className="font-mono font-bold">{formatTL(methods.wallet.amount)} · {methods.wallet.count} işlem</div>
                  </div>
                </CardContent>
              </Card>
            )}
            {methods.other?.amount > 0 && (
              <Card className="rounded-sm border-border">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Diğer / Eski</div>
                    <div className="font-mono font-bold">{formatTL(methods.other.amount)} · {methods.other.count} işlem</div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Regular / Subs / Unpaid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="rounded-sm border-border">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Coins size={20} weight="fill" className="text-primary" />
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Ücretli Tahsilat</div>
            </div>
            <div className="text-3xl font-bold font-mono" data-testid="kasa-regular-total">{formatTL(data.regular.total)}</div>
            <div className="text-xs text-muted-foreground">{data.regular.count} oturum</div>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-border">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkle size={20} weight="fill" className="text-muted-foreground" />
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Abonelik (Muaf)</div>
            </div>
            <div className="text-3xl font-bold font-mono" data-testid="kasa-subs-count">{data.subscribers.count}</div>
            <div className="text-xs text-muted-foreground">abone çıkış</div>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-destructive/40 bg-destructive/5">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={20} weight="fill" className="text-destructive" />
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Ödenmemiş</div>
            </div>
            <div className="text-3xl font-bold font-mono text-destructive" data-testid="kasa-unpaid-total">{formatTL(data.unpaid.total)}</div>
            <div className="text-xs text-muted-foreground">{data.unpaid.count} oturum</div>
          </CardContent>
        </Card>
      </div>

      {data.discount_count > 0 && (
        <Card
          className="rounded-sm border-amber-200 bg-amber-50 cursor-pointer hover:bg-amber-100 transition"
          onClick={() => navigate("/admin/payments-ledger?period=day&only_discount=1")}
          data-testid="kasa-discount-card"
        >
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-amber-800">Toplam Uygulanan İndirim (Bugün)</div>
              <div className="text-2xl font-mono font-bold text-amber-700">{formatTL(data.discount_total)}</div>
              <div className="text-xs text-amber-700/70">{data.discount_count} oturumda uygulandı</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-amber-700/70">Detay için Tahsilatlar</div>
              <div className="text-xs text-amber-800 font-semibold flex items-center justify-end gap-1">
                Görüntüle <ArrowRight size={12} weight="bold" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-widest">Rapor İndir</h3>
              <div className="text-xs text-muted-foreground mt-1">Gelir raporu — Excel ve PDF olarak</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Tabs value={period} onValueChange={setPeriod}>
                <TabsList className="rounded-sm">
                  <TabsTrigger value="daily" className="rounded-sm">Günlük</TabsTrigger>
                  <TabsTrigger value="weekly" className="rounded-sm">Haftalık</TabsTrigger>
                  <TabsTrigger value="monthly" className="rounded-sm">Aylık</TabsTrigger>
                </TabsList>
              </Tabs>
              <Button variant="outline" className="rounded-sm uppercase tracking-widest font-semibold" onClick={() => downloadFile("xlsx")} data-testid="export-xlsx-btn">
                <FileXls size={16} /> Excel
              </Button>
              <Button variant="outline" className="rounded-sm uppercase tracking-widest font-semibold" onClick={() => downloadFile("pdf")} data-testid="export-pdf-btn">
                <FilePdf size={16} /> PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
